/* eslint-disable max-lines, no-unused-vars */
import React, {
    useEffect, useMemo, useState
} from 'react';
import {
    Loader2, RotateCcw, AlertCircle, CheckCircle2, Globe, ChevronRight, SatelliteDish
} from 'lucide-react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Label
} from 'shared/components/ui/label';
import {
    cn
} from 'shared/lib/utils';
import {
    projectOService
} from 'shared/api/services/BEX';
import LongTextField from '../primitives/LongTextField';
import TypedField, {
    coerceTypedFieldValue
} from './TypedField';

/**
 * ApiTestPanel — call_and_validate live test for a saved API entity.
 *
 * Same pattern as OpcodeTestPanel + InstructionTestPanel. Schema-driven
 * (UF-13): renders one field per `api.paramsDef[i]` declared by the API,
 * grouped by `in` (path / query / header / body). Headers also get a
 * KeyValueEditor for any per-call header overrides not in paramsDef.
 *
 * On Send, builds the call_and_validate body with Authorization in headers:
 *   Bearer → Authorization: Bearer <token>
 *   Basic  → Authorization: Basic <base64(username:password)>
 *   {
 *     url:           <api.url with {path_var} substituted>,
 *     method:        api.method,
 *     headers:       { ...api.headers, ...overrides },
 *     timeout:       api.timeout,
 *     query_params:  { ... },
 *     body:          <JSON, only when method !== 'GET'>,
 *     output_schema: api.output_schema    (parsed JSON, optional)
 *   }
 *
 * POSTs to `projecto/config/api/v1/api_manager/call_and_validate` via
 * `projectOService.performRequestWithFullResponse` so we receive the full
 * downstream HTTP envelope (status, statusText, headers, data).
 *
 * No new endpoints (UF-2). The call uses the IN-MEMORY `api` prop — so an
 * in-progress edit of url/method/headers/timeout is testable without saving
 * (paramsDef-only test, since auth resolution is server-side via the saved doc).
 *
 * Layout:
 *   - Default (narrow): inputs on top, response below.
 *   - Maximized: inputs left (440px), response right (flex-1). 2-pane.
 */
export default function ApiTestPanel({
    api, maximized = false, runSignal = 0
}) {
    const apiUrl = api?.url || '';
    const apiMethod = (api?.method || 'GET').toUpperCase();
    const supportsBody = !['GET', 'HEAD'].includes(apiMethod);
    const baseHeaders = api?.headers || {};

    const decls = useMemo(() => normalizeParamsDef(api?.paramsDef), [api?.paramsDef]);
    // IMPORTANT: each slice must be memoized — useEffect deps below compare
    // by reference, and a fresh `.filter(...)` each render would loop
    // infinitely (filter → new array → useEffect → setState → render → repeat).
    const pathDecls = useMemo(() => decls.filter((d) => d.in === 'path'), [decls]);
    const queryDecls = useMemo(() => decls.filter((d) => d.in === 'query'), [decls]);
    const headerDecls = useMemo(() => decls.filter((d) => d.in === 'header'), [decls]);
    const bodyDecls = useMemo(() => decls.filter((d) => d.in === 'body'), [decls]);

    const [pathVals, setPathVals] = useState({});
    const [queryVals, setQueryVals] = useState({});
    const [headerVals, setHeaderVals] = useState({});
    const [bodyVals, setBodyVals] = useState({});
    const [bodyJson, setBodyJson] = useState(''); // free-form fallback when no body decls
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null); // { ok, status, body, raw, error }
    const [error, setError] = useState(null);

    // Seed defaults whenever the declared field set changes (e.g. user
    // edited paramsDef in the form on the left).
    useEffect(() => { setPathVals((p) => seedDefaults(pathDecls, p)); }, [pathDecls]);
    useEffect(() => { setQueryVals((p) => seedDefaults(queryDecls, p)); }, [queryDecls]);
    useEffect(() => { setHeaderVals((p) => seedDefaults(headerDecls, p)); }, [headerDecls]);
    useEffect(() => { setBodyVals((p) => seedDefaults(bodyDecls, p)); }, [bodyDecls]);

    // Fire run() when the parent bumps `runSignal` (ApiForm's request-bar Send).
    // Guarded so the initial 0 doesn't auto-run on mount.
    useEffect(() => {
        if (runSignal > 0) run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runSignal]);

    const totalSet =
        countSet(pathVals) + countSet(queryVals) + countSet(headerVals)
        + countSet(bodyVals) + (bodyJson.trim() ? 1 : 0);
    const totalNeeded = pathDecls.length + queryDecls.length + headerDecls.length + bodyDecls.length;

    function clearAll() {
        setPathVals(seedDefaults(pathDecls, {}));
        setQueryVals(seedDefaults(queryDecls, {}));
        setHeaderVals(seedDefaults(headerDecls, {}));
        setBodyVals(seedDefaults(bodyDecls, {}));
        setBodyJson('');
        setResult(null);
        setError(null);
    }

    // Substitute path params into the URL. Handles all three placeholder forms:
    //   {{name}} (engine Jinja2) · {name} (single brace) · :name (Postman).
    // Double-brace MUST run first, else `{{name}}` becomes `{value}` (the inner
    // `{name}` matches the single-brace pattern and leaves the outer braces).
    function substitutePath(url, values) {
        let u = url || '';
        for (const [k, v] of Object.entries(values || {})) {
            if (v == null || v === '') continue;
            const enc = encodeURIComponent(String(v));
            const key = escapeRe(k);
            u = u
                .replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), enc)
                .replace(new RegExp(`\\{\\s*${key}\\s*\\}`, 'g'), enc)
                .replace(new RegExp(`:${key}(?![A-Za-z0-9_])`, 'g'), enc);
        }
        return u;
    }

    function buildBody() {
        // Prefer declared body fields when present.
        if (bodyDecls.length > 0) {
            const out = {};
            for (const f of bodyDecls) {
                const raw = bodyVals[f.name];
                if (raw === '' || raw == null) continue;
                out[f.name] = coerceTypedFieldValue(raw, f.type);
            }
            return out;
        }
        // Otherwise the free-form JSON fallback.
        const text = bodyJson.trim();
        if (!text) return undefined;
        try { return JSON.parse(text); }
        catch { return text; }
    }

    async function run() {
        if (running) return;
        if (!apiUrl) {
            setError('No URL on this API. Save url + method first.');
            return;
        }
        setError(null);
        setRunning(true);
        setResult(null);

        // Resolve path params client-side; fall back to original URL if any
        // path token is empty (server's call_and_validate will surface the error).
        const finalUrl = substitutePath(apiUrl, pathVals);
        const headers = {
            ...baseHeaders
        };
        for (const [k, v] of Object.entries(headerVals)) {
            if (v !== '' && v != null) headers[k] = String(v);
        }
        const vaultAuth = isVaultBackedAuth(api?.auth);
        const authApplied = applyAuthorizationHeader(headers, api?.auth);
        if (!vaultAuth) {
            if (api?.auth?.type === 'basic' && !authApplied) {
                setError('Basic auth needs a username in the Authorization tab.');
                setRunning(false);
                return;
            }
            if (api?.auth?.type === 'bearer' && !authApplied) {
                setError('Bearer auth needs a token in the Authorization tab.');
                setRunning(false);
                return;
            }
        } else if (!api?.organizationId) {
            setError('Vault-backed auth needs organizationId on the API to resolve secrets at test time.');
            setRunning(false);
            return;
        }
        const queryParams = {};
        for (const [k, v] of Object.entries(queryVals)) {
            if (v !== '' && v != null) queryParams[k] = String(v);
        }
        const data = {
            url: finalUrl,
            method: apiMethod,
            headers,
            query_params: queryParams,
            timeout: api?.timeout || 10000
        };
        // Vault refs: send auth.*_ref + org so Mind resolves (never plaintext in Redis/UI).
        if (vaultAuth) {
            data.auth = {
                type: api.auth.type,
                ...(api.auth.token_ref ? { token_ref: api.auth.token_ref } : {}),
                ...(api.auth.password_ref ? { password_ref: api.auth.password_ref } : {}),
                ...(api.auth.value_ref ? { value_ref: api.auth.value_ref } : {}),
                ...(api.auth.username ? { username: api.auth.username } : {}),
                ...(api.auth.key ? { key: api.auth.key } : {}),
            };
            data.organization_id = api.organizationId;
            if (api._id) data.api_id = api._id;
        }
        if (supportsBody) {
            const b = buildBody();
            if (b !== undefined) data.body = b;
        }
        if (api?.output_schema) {
            try {
                const os = typeof api.output_schema === 'string'
                    ? JSON.parse(api.output_schema)
                    : api.output_schema;
                data.output_schema = os;
            } catch { /* ignore — server validates anyway */ }
        }

        const startedAt = performance.now();
        try {
            const res = await projectOService.performRequestWithFullResponse(
                'projecto/config/api/v1/api_manager/call_and_validate',
                {
                    method: 'POST',
                    data
                }
            );
            const elapsed = Math.round(performance.now() - startedAt);
            setResult({
                ok: res?.status >= 200 && res?.status < 400,
                status: res?.status,
                statusText: res?.statusText,
                headers: safeFromHeaders(res?.headers),
                body: res?.data,
                elapsed,
                request: data,
                raw: res
            });
        } catch (err) {
            const elapsed = Math.round(performance.now() - startedAt);
            setResult({
                ok: false,
                status: err?.response?.status,
                statusText: err?.response?.statusText,
                body: err?.response?.data,
                elapsed,
                request: data,
                error: err?.message || String(err)
            });
        } finally {
            setRunning(false);
        }
    }

    return (
        <div className={cn(
            'flex h-full overflow-hidden',
            maximized ? 'flex-col lg:flex-row' : 'flex-col'
        )}>
            {/* ── INPUTS PANE ───────────────────────────────────── */}
            <div className={cn(
                'flex flex-col min-h-0 bg-indigo-50/30',
                maximized ? 'lg:w-[440px] lg:flex-shrink-0 lg:border-r lg:border-indigo-100/80' : 'border-b border-indigo-100/80 max-h-[60%]'
            )}>
                {/* API summary header */}
                <div className="flex-shrink-0 border-b border-indigo-100/80 px-3 py-2.5">
                    <div className="rounded-lg border border-indigo-200/50 bg-white/90 px-3 py-2 shadow-sm">
                        <div className="flex items-start gap-2">
                            <Globe className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-500" />
                            <div className="min-w-0 flex-1 space-y-1">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="rounded-md bg-indigo-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-indigo-800">
                                        {apiMethod}
                                    </span>
                                    <span className="truncate text-[12.5px] font-semibold text-foreground">
                                        {api?.name || api?._id || '(unsaved)'}
                                    </span>
                                </div>
                                <div
                                    className="truncate font-mono text-[10.5px] text-muted-foreground"
                                    title={apiUrl}
                                >
                                    {apiUrl || '(no URL)'}
                                </div>
                                {api?.timeout && (
                                    <div className="text-[10px] text-muted-foreground">
                                        timeout {api.timeout}ms
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-4">
                    {error && (
                        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-[12px] text-destructive">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {pathDecls.length > 0 && (
                        <Section title="Path params" hint={`${pathDecls.length} declared`}>
                            {pathDecls.map((f) => (
                                <TypedField
                                    key={f.name}
                                    name={f.name}
                                    type={f.type || 'string'}
                                    required={!!f.required}
                                    description={f.description}
                                    value={pathVals[f.name] ?? ''}
                                    onChange={(v) => setPathVals((p) => ({
                                        ...p,
                                        [f.name]: v
                                    }))}
                                />
                            ))}
                        </Section>
                    )}

                    {queryDecls.length > 0 && (
                        <Section title="Query params" hint={`${queryDecls.length} declared`}>
                            {queryDecls.map((f) => (
                                <TypedField
                                    key={f.name}
                                    name={f.name}
                                    type={f.type || 'string'}
                                    required={!!f.required}
                                    description={f.description}
                                    value={queryVals[f.name] ?? ''}
                                    onChange={(v) => setQueryVals((p) => ({
                                        ...p,
                                        [f.name]: v
                                    }))}
                                />
                            ))}
                        </Section>
                    )}

                    {headerDecls.length > 0 && (
                        <Section title="Header overrides" hint={`${headerDecls.length} declared`}>
                            {headerDecls.map((f) => (
                                <TypedField
                                    key={f.name}
                                    name={f.name}
                                    type={f.type || 'string'}
                                    required={!!f.required}
                                    description={f.description}
                                    value={headerVals[f.name] ?? ''}
                                    onChange={(v) => setHeaderVals((p) => ({
                                        ...p,
                                        [f.name]: v
                                    }))}
                                />
                            ))}
                        </Section>
                    )}

                    {supportsBody && bodyDecls.length > 0 && (
                        <Section title="Body fields" hint={`${bodyDecls.length} declared`}>
                            {bodyDecls.map((f) => (
                                <TypedField
                                    key={f.name}
                                    name={f.name}
                                    type={f.type || 'string'}
                                    required={!!f.required}
                                    description={f.description}
                                    value={bodyVals[f.name] ?? ''}
                                    onChange={(v) => setBodyVals((p) => ({
                                        ...p,
                                        [f.name]: v
                                    }))}
                                />
                            ))}
                        </Section>
                    )}

                    {supportsBody && bodyDecls.length === 0 && (
                        <Section title="Body" hint="raw JSON · opens in a popup">
                            <LongTextField
                                label="Request body (JSON)"
                                value={bodyJson}
                                onChange={setBodyJson}
                                placeholder='{ "key": "value" }'
                                monospace
                            />
                        </Section>
                    )}

                    {totalNeeded === 0 && !supportsBody && (
                        <div className="rounded-md border border-dashed border-indigo-200/80 bg-white/60 px-3 py-2 text-[11.5px] text-muted-foreground">
                            No parameters declared — use <b>Send</b> in the request bar to call this endpoint.
                        </div>
                    )}
                </div>

                <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-indigo-100/80 bg-white/50 px-3 py-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAll}
                        disabled={running || (totalSet === 0 && !result)}
                    >
                        <RotateCcw className="mr-1 h-3 w-3" /> Clear inputs
                    </Button>
                </div>
            </div>

            {/* ── RESPONSE PANE ─────────────────────────────────── */}
            <div className="flex-1 min-h-0 overflow-y-auto bg-indigo-50/20 p-4">
                <ResultBlock running={running} result={result} method={apiMethod} />
            </div>
        </div>
    );
}

// ─── Sub-components ───────────────────────────────────────────────────

function Section({
    title, hint, children
}) {
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between border-b border-dashed pb-0.5">
                <h4 className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{title}</h4>
                {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
            </div>
            <div className="space-y-2.5">{children}</div>
        </div>
    );
}

function ResultBlock({
    running, result, method
}) {
    if (!running && !result) {
        return (
            <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-indigo-200/60 bg-white/80 shadow-sm">
                    <SatelliteDish className="h-7 w-7 text-indigo-400/90" strokeWidth={1.5} />
                </div>
                <p className="text-[13px] font-medium text-foreground">Ready to send</p>
                <p className="mt-1 max-w-[280px] text-[12px] leading-relaxed text-muted-foreground">
                    Fill any path, query, or body inputs below, then click <b>Send</b> in the request bar.
                    Status, headers, and response body will appear here.
                </p>
            </div>
        );
    }
    if (running) {
        return (
            <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2.5 text-sm italic text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> calling…
            </div>
        );
    }

    const ok = result.ok;
    const statusBadgeTone = ok ? 'emerald' : 'destructive';

    return (
        <div className="space-y-3">
            <div className={cn(
                'rounded-md border px-3 py-2 flex items-start gap-2',
                ok
                    ? 'border-emerald-300/50 bg-emerald-50/40'
                    : 'border-destructive/40 bg-destructive/5'
            )}>
                {ok
                    ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
                    : <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
                }
                <div className="min-w-0 flex-1">
                    <div className={cn(
                        'text-[12.5px] font-semibold',
                        ok ? 'text-emerald-800' : 'text-destructive'
                    )}>
                        {ok ? 'Completed' : 'Failed'}
                        {result.status != null && (
                            <span className="ml-1.5 font-mono text-[11.5px]">
                                {result.status} {result.statusText || ''}
                            </span>
                        )}
                    </div>
                    <div className="text-[10.5px] text-muted-foreground">
                        {method} {result.elapsed != null ? `· ${result.elapsed}ms` : ''}
                    </div>
                    {result.error && (
                        <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[11px] text-destructive">
                            {result.error}
                        </pre>
                    )}
                </div>
            </div>

            <div className="rounded-md border bg-card px-3 py-2.5">
                <div className="mb-1 flex items-center gap-1.5">
                    <Globe className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">response body</span>
                </div>
                <pre className="whitespace-pre-wrap break-words text-[12.5px] leading-snug text-foreground max-h-[40vh] overflow-y-auto">
                    {safeStringify(result.body)}
                </pre>
            </div>

            {result.headers && Object.keys(result.headers).length > 0 && (
                <details className="rounded-md border bg-card px-3 py-2 text-[11.5px]">
                    <summary className="cursor-pointer flex items-center gap-1 text-muted-foreground hover:text-foreground">
                        <ChevronRight className="h-3 w-3" />
                        Response headers ({Object.keys(result.headers).length})
                    </summary>
                    <pre className="mt-2 max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded border bg-muted/20 px-2 py-1.5 font-mono text-[10.5px]">
                        {safeStringify(result.headers)}
                    </pre>
                </details>
            )}

            <details className="rounded-md border bg-card px-3 py-2 text-[11.5px]">
                <summary className="cursor-pointer flex items-center gap-1 text-muted-foreground hover:text-foreground">
                    <ChevronRight className="h-3 w-3" />
                    Request sent
                </summary>
                <pre className="mt-2 max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded border bg-muted/20 px-2 py-1.5 font-mono text-[10.5px]">
                    {safeStringify(result.request)}
                </pre>
            </details>
        </div>
    );
}

// ─── Helpers ───────────────────────────────────────────────────────

function encodeBasicAuth(username, password) {
    const creds = `${username ?? ''}:${password ?? ''}`;
    if (typeof TextEncoder !== 'undefined') {
        const bytes = new TextEncoder().encode(creds);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        return `Basic ${btoa(binary)}`;
    }
    return `Basic ${btoa(creds)}`;
}

function isVaultBackedAuth(apiAuth) {
    if (!apiAuth || typeof apiAuth !== 'object') return false;
    return Boolean(
        apiAuth.token_ref
        || apiAuth.password_ref
        || apiAuth.value_ref
        || apiAuth.secrets_configured
    );
}

/**
 * Authorization tab → outbound request headers (Postman-style).
 * Bearer: Authorization: Bearer <token>
 * Basic:  Authorization: Basic <base64(username:password)>
 * Wins over any Authorization key from the static Headers tab.
 * Vault-backed auth (refs only) returns true without setting headers —
 * the caller sends auth.*_ref to Mind for resolve.
 */
function applyAuthorizationHeader(headers, apiAuth) {
    if (!apiAuth?.type || apiAuth.type === 'none') return true;
    if (isVaultBackedAuth(apiAuth) && !apiAuth.token && !apiAuth.password && !apiAuth.value) {
        return true;
    }
    const type = String(apiAuth.type).toLowerCase();
    if (type === 'bearer') {
        const token = (apiAuth.token || '').trim();
        if (!token) return false;
        headers.Authorization = `Bearer ${token}`;
        return true;
    }
    if (type === 'basic') {
        const username = apiAuth.username ?? '';
        const password = apiAuth.password ?? '';
        if (!String(username).trim() && !String(password)) return false;
        headers.Authorization = encodeBasicAuth(username, password);
        return true;
    }
    return true;
}

function normalizeParamsDef(arr) {
    if (!Array.isArray(arr)) return [];
    return arr
        .map((p) => {
            if (typeof p === 'string') {
                return {
                    name: p,
                    in: 'query',
                    type: 'string'
                };
            }
            const obj = p || {};
            // Legacy/UI shape from ApiForm's "Query Params" tab: rows are
            // stored as { key, value } with no `in`. Treat each as a query
            // param with `value` as the default seed for the test panel.
            if (!obj.name && obj.key) {
                return {
                    name: obj.key,
                    in: obj.in || 'query',
                    type: obj.type || 'string',
                    default: obj.value
                };
            }
            return {
                ...obj
            };
        })
        .filter((p) => p && p.name);
}

function seedDefaults(decls, prev) {
    const next = {};
    for (const f of decls) {
        if (prev[f.name] !== undefined) {
            next[f.name] = prev[f.name];
        } else {
            next[f.name] = f.default !== undefined && f.default !== null
                ? String(f.default)
                : '';
        }
    }
    return next;
}

function countSet(map) {
    return Object.values(map).filter((v) => v !== '' && v != null).length;
}

function escapeRe(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function safeFromHeaders(h) {
    if (!h) return null;
    if (typeof h.entries === 'function') {
        try { return Object.fromEntries([...h.entries()]); } catch { /* fallthrough */ }
    }
    if (typeof h === 'object') return {
        ...h
    };
    return null;
}

function safeStringify(v) {
    if (v == null) return '(empty)';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}
